import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Artist, UserRole, ViewMode, SortOption, Project, User, canDeleteArtists, isClientRole } from './types';
import { ARTISTS_DATA } from './constants';
import { API_CONFIG } from './config';
import { apiGet, apiFetch, apiPost, apiPut, apiDelete } from './utils/api';
import {
  getUserPreferences,
  loadUserPreferencesFromServer,
  subscribeUserPreferences,
  updateUserPreferences,
  isPreferencesReadyForSync,
} from './utils/userPreferences';
import LoginScreen from './components/LoginScreen';
import Header from './components/Header';
import ViewModeToggle from './components/ViewModeToggle';
import ContentViewToggle, { ContentView } from './components/ContentViewToggle';
import SortToggle from './components/SortToggle';
import FilterBar, { Filters } from './components/FilterBar';
import ArtistList from './components/ArtistList';
import ProjectList from './components/ProjectList';
import TeamsView from './components/TeamsView';
import RatesView from './components/RatesView';
import BiddingList from './components/BiddingList';
import BiddingModal from './components/BiddingModal';
import ShotTable from './components/ShotTable';
import ArtistFormModal from './components/ArtistFormModal';
import ProfileWizardModal from './components/ProfileWizardModal';
import ProjectManagementModal from './components/ProjectManagementModal';
import UserManagementModal from './components/UserManagementModal';
import ProjectInvitationModal from './components/ProjectInvitationModal';
import AddUsersToProjectModal from './components/AddUsersToProjectModal';
import VfxPlannerStub from './components/VfxPlannerStub';
import SystemMenu from './components/SystemMenu';
import ConfirmationModal from './components/ui/ConfirmationModal';
import NotificationContainer from './components/ui/NotificationContainer';
import { useNotifications } from './hooks/useNotifications';
import { usePendingAccessRequests } from './hooks/usePendingAccessRequests';
import { usePendingBiddingRevisions } from './hooks/usePendingBiddingRevisions';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import TransactionsPage from './components/TransactionsPage';
import ExportBiddingExcelModal from './components/ExportBiddingExcelModal';
import type { ExcelExportAudience } from './utils/excelExport';
// import TestSpinner from './components/TestSpinner'; // Временно отключен

// Helper function to map user role string to UserRole enum
const mapUserRole = (roleString: string): UserRole => {
  switch (roleString) {
    case 'Super Admin':
      return UserRole.SUPER_ADMIN;
    case 'Admin':
      return UserRole.ADMIN;
    case 'Producer':
      return UserRole.PRODUCER;
    case 'Project Manager':
      return UserRole.PROJECT_MANAGER;
    case 'Coordinator':
      return UserRole.COORDINATOR;
    case 'Client':
      return UserRole.CLIENT;
    case 'Sales':
      return UserRole.SALES;
    case 'Demo':
      return UserRole.DEMO;
    default:
      console.warn(`Unknown role: ${roleString}, falling back to Demo`);
      return UserRole.DEMO; // fallback to most restricted role
  }
};

// Load custom theme from localStorage and apply to DOM
const loadCustomTheme = () => {
  const savedTheme = localStorage.getItem('spellbook_custom_theme');
  if (savedTheme) {
    try {
      const theme = JSON.parse(savedTheme);
      const root = document.documentElement;
      
      // Apply all custom colors as CSS variables
      Object.entries(theme).forEach(([key, value]) => {
        // Convert camelCase to kebab-case, handling numbers correctly
        // gray700 -> gray-700, textSecondary -> text-secondary
        let cssVarName = key
          .replace(/([A-Z])/g, '-$1') // Add dash before uppercase letters
          .replace(/([a-z])(\d)/g, '$1-$2') // Add dash before numbers
          .replace(/(\d)([a-z])/g, '$1-$2') // Add dash after numbers before letters
          .toLowerCase();
        
        root.style.setProperty(`--color-${cssVarName}`, value as string);
      });
      
      console.log('🎨 Custom theme loaded from localStorage');
    } catch (error) {
      console.error('Failed to load custom theme:', error);
    }
  }
};

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ biddingSheetId?: string; projectId?: string; shotId?: string }>();
  
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const {
    notifications,
    removeNotification,
    removeByPersistentId,
    addNotification,
    upsertByPersistentId,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  } = useNotifications();

  usePendingAccessRequests({
    userRole: user?.role,
    addNotification,
    removeByPersistentId,
    showSuccess,
    showError,
  });

  usePendingBiddingRevisions({
    userRole: user?.role,
    addNotification,
    upsertByPersistentId,
    removeByPersistentId,
    showSuccess,
    showError,
  });
  const [artists, setArtists] = useState<Artist[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [biddingSheets, setBiddingSheets] = useState<any[]>([]);
  const [isArtistModalOpen, setIsArtistModalOpen] = useState(false);
  const [isProfileWizardOpen, setIsProfileWizardOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isSystemMenuOpen, setIsSystemMenuOpen] = useState(false);
  const [isBiddingModalOpen, setIsBiddingModalOpen] = useState(false);
  const [isProjectInvitationModalOpen, setIsProjectInvitationModalOpen] = useState(false);
  const [artistsToInvite, setArtistsToInvite] = useState<string[]>([]);
  const [biddingMode, setBiddingMode] = useState<'video' | 'manual'>('video');
  const [addUsersModal, setAddUsersModal] = useState<{ isOpen: boolean; projectId: string; projectName: string }>({
    isOpen: false,
    projectId: '',
    projectName: ''
  });
  const [artistToEdit, setArtistToEdit] = useState<Artist | null>(null);
  const [selectedProjectForBidding, setSelectedProjectForBidding] = useState<Project | null>(null);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [selectedBiddingSheet, setSelectedBiddingSheet] = useState<any | null>(null);
  const defaultFilters: Filters = {
    name: '',
    specialization: '',
    software: [],
    availableToday: false,
    project: 'ALL_PROFILES',
    status: '',
    priority: '',
    projectType: '',
    client: '',
    producerId: '',
    managerId: '',
    biddingProject: '',
    profileType: '',
    registrationStatus: '',
  };

  const parseViewMode = (value?: string): ViewMode | undefined => {
    if (value === ViewMode.LIST || value === ViewMode.GRID) return value;
    return undefined;
  };

  const parseSortOption = (value?: string): SortOption | undefined => {
    if (
      value === SortOption.NAME_ASC ||
      value === SortOption.NAME_DESC ||
      value === SortOption.SPECIALIZATION_ASC ||
      value === SortOption.SPECIALIZATION_DESC
    ) {
      return value;
    }
    return undefined;
  };

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const prefs = getUserPreferences();
    return parseViewMode(prefs.viewModes?.artists) ?? ViewMode.GRID;
  });
  const [projectViewMode, setProjectViewMode] = useState<ViewMode>(() => {
    const prefs = getUserPreferences();
    return parseViewMode(prefs.viewModes?.projects) ?? ViewMode.GRID;
  });
  const [biddingViewMode, setBiddingViewMode] = useState<ViewMode>(() => {
    const prefs = getUserPreferences();
    return parseViewMode(prefs.viewModes?.bidding) ?? ViewMode.GRID;
  });
  const [activeUsers, setActiveUsers] = useState<Array<{ userId: string; username: string; email: string; currentView?: string; biddingSheetId?: string | null }>>([]);
  // contentView is now derived from URL path
  const getContentViewFromPath = (pathname: string): ContentView => {
    if (pathname.startsWith('/finance')) return 'finance';
    if (pathname.startsWith('/bidding')) return 'bidding';
    if (pathname.startsWith('/projects')) return 'projects';
    if (pathname.startsWith('/rates')) return 'rates';
    if (pathname.startsWith('/teams')) return 'teams';
    if (pathname.startsWith('/artists')) return 'artists';
    if (pathname.startsWith('/users')) return 'users';
    return 'projects'; // default - projects is accessible to all roles
  };
  const contentView = getContentViewFromPath(location.pathname);
  const [sortOption, setSortOption] = useState<SortOption>(() => {
    const prefs = getUserPreferences();
    return parseSortOption(prefs.sort?.artists) ?? SortOption.NAME_ASC;
  });
  const [projectSortOption, setProjectSortOption] = useState<SortOption>(() => {
    const prefs = getUserPreferences();
    return parseSortOption(prefs.sort?.projects) ?? SortOption.NAME_ASC;
  });
  const [teamsSortOption, setTeamsSortOption] = useState<SortOption>(() => {
    const prefs = getUserPreferences();
    return parseSortOption(prefs.sort?.teams) ?? SortOption.NAME_ASC;
  });
  const [biddingSortOption, setBiddingSortOption] = useState<SortOption>(() => {
    const prefs = getUserPreferences();
    return parseSortOption(prefs.sort?.bidding) ?? SortOption.NAME_ASC;
  });
  const [selectedArtistIds, setSelectedArtistIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(() => {
    const prefs = getUserPreferences();
    return { ...defaultFilters, ...(prefs.filters || {}) };
  });
  
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    updateUserPreferences({ viewModes: { artists: mode } });
  };

  const handleProjectViewModeChange = (mode: ViewMode) => {
    setProjectViewMode(mode);
    updateUserPreferences({ viewModes: { projects: mode } });
  };

  const handleBiddingViewModeChange = (mode: ViewMode) => {
    setBiddingViewMode(mode);
    updateUserPreferences({ viewModes: { bidding: mode } });
  };

  useEffect(() => {
    return subscribeUserPreferences((prefs) => {
      const artistsView = parseViewMode(prefs.viewModes?.artists);
      if (artistsView) setViewMode((prev) => (prev === artistsView ? prev : artistsView));
      const projectsView = parseViewMode(prefs.viewModes?.projects);
      if (projectsView) setProjectViewMode((prev) => (prev === projectsView ? prev : projectsView));
      const biddingView = parseViewMode(prefs.viewModes?.bidding);
      if (biddingView) setBiddingViewMode((prev) => (prev === biddingView ? prev : biddingView));

      const artistsSort = parseSortOption(prefs.sort?.artists);
      if (artistsSort) setSortOption((prev) => (prev === artistsSort ? prev : artistsSort));
      const projectsSort = parseSortOption(prefs.sort?.projects);
      if (projectsSort) setProjectSortOption((prev) => (prev === projectsSort ? prev : projectsSort));
      const teamsSort = parseSortOption(prefs.sort?.teams);
      if (teamsSort) setTeamsSortOption((prev) => (prev === teamsSort ? prev : teamsSort));
      const biddingSort = parseSortOption(prefs.sort?.bidding);
      if (biddingSort) setBiddingSortOption((prev) => (prev === biddingSort ? prev : biddingSort));

      if (prefs.filters) {
        setFilters((prev) => {
          const next = { ...prev, ...prefs.filters };
          return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!user || !isPreferencesReadyForSync()) return;
    const prefs = getUserPreferences();
    if (prefs.sort?.artists === sortOption) return;
    updateUserPreferences({ sort: { artists: sortOption } });
  }, [sortOption, user]);

  useEffect(() => {
    if (!user || !isPreferencesReadyForSync()) return;
    const prefs = getUserPreferences();
    if (prefs.sort?.projects === projectSortOption) return;
    updateUserPreferences({ sort: { projects: projectSortOption } });
  }, [projectSortOption, user]);

  useEffect(() => {
    if (!user || !isPreferencesReadyForSync()) return;
    const prefs = getUserPreferences();
    if (prefs.sort?.teams === teamsSortOption) return;
    updateUserPreferences({ sort: { teams: teamsSortOption } });
  }, [teamsSortOption, user]);

  useEffect(() => {
    if (!user || !isPreferencesReadyForSync()) return;
    const prefs = getUserPreferences();
    if (prefs.sort?.bidding === biddingSortOption) return;
    updateUserPreferences({ sort: { bidding: biddingSortOption } });
  }, [biddingSortOption, user]);

  useEffect(() => {
    if (!user || !isPreferencesReadyForSync()) return;
    const prefs = getUserPreferences();
    if (JSON.stringify(prefs.filters ?? {}) === JSON.stringify(filters)) return;
    updateUserPreferences({ filters });
  }, [filters, user]);

  // Состояние для подтверждения удаления художника
  const [deleteArtistConfirmation, setDeleteArtistConfirmation] = useState<{
    isOpen: boolean;
    artistId: string | null;
    artistName: string | null;
  }>({
    isOpen: false,
    artistId: null,
    artistName: null
  });

  // Состояние для подтверждения удаления проекта
  const [deleteProjectConfirmation, setDeleteProjectConfirmation] = useState<{
    isOpen: boolean;
    projectId: string | null;
    projectName: string | null;
  }>({
    isOpen: false,
    projectId: null,
    projectName: null
  });

  useEffect(() => {
    const initializeApp = async () => {
      console.log('🚀 Initializing SpellBook DB...');

      loadCustomTheme();

      await loadUserPreferencesFromServer();
      loadCustomTheme();
      
      // Add artificial delay to show spinner
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Check if user is already logged in (token in localStorage)
      const token = localStorage.getItem('spellbook_token')
      const userData = localStorage.getItem('spellbook_user')
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData)
          console.log('👤 Found existing user session:', parsedUser.email);

          await loadUserPreferencesFromServer();
          loadCustomTheme();

          setUser(parsedUser)
          setCurrentRole(parsedUser.role);
          
          // Load data from API after login
          await fetchData();
          
          // Handle direct link navigation after data is loaded
          handleDirectLinkNavigation();
        } catch (error) {
          console.error('❌ Error parsing user data or loading data:', error)
          // Reset state to ensure we show login screen
          setUser(null);
          setCurrentRole(null);
          
          // If error is token expiration, apiGet already redirected to login
          if (error instanceof Error && (error.message.includes('Token expired') || error.message.includes('Authentication failed'))) {
            // Already redirected, just clear local storage
          localStorage.removeItem('spellbook_token')
          localStorage.removeItem('spellbook_user')
            return;
          }
          // For other errors, clear session
          localStorage.removeItem('spellbook_token')
          localStorage.removeItem('spellbook_user')
        }
      }
      
      console.log('✅ App initialization complete');
      setLoading(false)
    };
    
    initializeApp();
  }, [])

  // Redirect /users to /artists (unified profiles view)
  useEffect(() => {
    if (!currentRole || loading) return;
    
    // Redirect legacy /users route to /artists
    if (location.pathname.startsWith('/users')) {
      console.log(`[Access] Redirecting /users to /artists`);
      navigate('/artists', { replace: true });
    }
  }, [currentRole, location.pathname, loading, navigate])

  // Heartbeat: отправка активности пользователя и получение активных пользователей
  useEffect(() => {
    if (!user || !currentRole) return;

    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('spellbook_token');
        if (!token) return;

        // Определяем текущий view и biddingSheetId
        const currentView = contentView;
        const biddingSheetId = contentView === 'bidding' && selectedBiddingSheet ? selectedBiddingSheet.id : null;

        console.log('[Heartbeat] Sending activity:', { currentView, biddingSheetId, userId: user?.id });

        // Отправляем heartbeat
        await apiPost(`${API_CONFIG.API_URL}/auth/activity`, {
          currentView,
          biddingSheetId: biddingSheetId || null // Явно указываем null если нет sheet
        });

        // Получаем ВСЕХ активных пользователей (без фильтрации по контексту)
        const activeUsersResponse = await apiGet(`${API_CONFIG.API_URL}/auth/activity`);

        if (activeUsersResponse.ok) {
          const data = await activeUsersResponse.json();
          console.log('[Heartbeat] Received active users:', data.activeUsers);
          setActiveUsers(data.activeUsers || []);
        }
      } catch (error) {
        // If token expired, apiPost/apiGet already redirected to login
        if (error instanceof Error && (error.message.includes('Token expired') || error.message.includes('Authentication failed'))) {
          console.log('Token expired during heartbeat, user will be redirected to login');
          return;
        }
        console.error('Error sending heartbeat:', error);
      }
    };

    // Отправляем heartbeat сразу
    sendHeartbeat();

    // Затем каждые 5 секунд
    const intervalId = setInterval(sendHeartbeat, 5000);

    // Очистка при размонтировании или logout
    return () => {
      clearInterval(intervalId);
      // Отправляем сигнал о выходе (опционально)
      if (user?.id) {
        apiPost(`${API_CONFIG.API_URL}/auth/activity`, {
          currentView: null,
          biddingSheetId: null
        }).catch(() => {});
      }
    };
  }, [user, currentRole, contentView, selectedBiddingSheet]);

  // Handle direct link navigation - load data based on URL params
  const handleDirectLinkNavigation = async () => {
    if (!user || loading) return;
    
    // Handle bidding sheet direct link
    if (params.biddingSheetId) {
      // Try to find bidding sheet in loaded list first
      let biddingSheet = biddingSheets.find(b => b.id === params.biddingSheetId);
      
      // If not found, try fetching it from API
      if (!biddingSheet) {
        try {
          const response = await apiGet(`${API_CONFIG.API_URL}/bidding/${params.biddingSheetId}`);
          if (response.ok) {
            biddingSheet = await response.json();
            // Add to bidding sheets list if not already there
            setBiddingSheets(prev => {
              if (!prev.find(b => b.id === biddingSheet.id)) {
                return [...prev, biddingSheet];
              }
              return prev;
            });
          } else {
            // Bidding sheet not found, redirect to bidding list
            navigate('/bidding', { replace: true });
            setSelectedBiddingSheet(null);
            return;
          }
        } catch (error) {
          console.error('Failed to fetch bidding sheet:', error);
          // On error, redirect to bidding list
          navigate('/bidding', { replace: true });
          setSelectedBiddingSheet(null);
          return;
        }
      }
      
      if (biddingSheet) {
        // Only update if different to avoid unnecessary re-renders
        if (selectedBiddingSheet?.id !== biddingSheet.id) {
          setSelectedBiddingSheet(biddingSheet);
        }
        // If shotId is present, ShotTable will handle opening the annotation modal
      }
    } else if (location.pathname === '/bidding' && !params.biddingSheetId) {
      // Only clear selected sheet if we're explicitly on the bidding list (not during navigation)
      // Don't clear if we're navigating to a specific bidding sheet
      if (selectedBiddingSheet && !params.biddingSheetId) {
        setSelectedBiddingSheet(null);
      }
    }
    
    // Handle project direct link (if project details view exists)
    if (params.projectId) {
      const project = projects.find(p => p.id === params.projectId);
      if (!project && projects.length > 0) {
        // Project not found and we have loaded projects, redirect to projects list
        navigate('/projects');
      }
    }
    
    // Redirect root path to artists view
    if (location.pathname === '/' || location.pathname === '') {
      navigate('/artists', { replace: true });
    }
  };

  // Sync URL changes with component state
  useEffect(() => {
    if (!user || loading) return;
    handleDirectLinkNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, params.biddingSheetId, params.projectId, params.shotId, biddingSheets.length, projects.length]);
  
  // Authentication guard - redirect to login if not authenticated
  useEffect(() => {
    const token = localStorage.getItem('spellbook_token');
    const userData = localStorage.getItem('spellbook_user');
    
    // Don't redirect if on public routes
    if (location.pathname.startsWith('/apply/') || location.pathname === '/reset-password') {
      return;
    }
    
    if (!token || !userData) {
      // Not authenticated and not on public route - stay on login screen
      // LoginScreen will be shown by conditional rendering below
      return;
    }
  }, [location.pathname, user]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('spellbook_token')
      if (!token) return;

      // Get current user role from localStorage
      const userData = localStorage.getItem('spellbook_user');
      const userRole = userData ? JSON.parse(userData).role : null;
      const canAccessUsers = userRole === 'Super Admin' || userRole === 'Admin';

      console.log('🔍 Fetching data from API... (role:', userRole, ')');
      
      // Only fetch users if the role has permission
      const fetchPromises = [
        apiGet(API_CONFIG.ARTISTS.LIST),
        apiGet(API_CONFIG.PROJECTS.LIST),
        apiGet(`${API_CONFIG.API_URL}/bidding`)
      ];
      
      if (canAccessUsers) {
        fetchPromises.push(apiGet(API_CONFIG.AUTH.USERS));
      }
      
      const responses = await Promise.all(fetchPromises);
      const [artistsRes, projectsRes, biddingRes] = responses;
      const usersRes = canAccessUsers ? responses[3] : null;

      if (artistsRes.ok) {
        const artistsData = await artistsRes.json()
        console.log('✅ Artists loaded:', artistsData);
        setArtists(Array.isArray(artistsData) ? artistsData : artistsData.artists || [])
      } else {
        console.error('❌ Failed to fetch artists:', await artistsRes.text())
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json()
        console.log('✅ Projects loaded:', projectsData);
        setProjects(Array.isArray(projectsData) ? projectsData : projectsData.projects || [])
      } else {
        console.error('❌ Failed to fetch projects:', await projectsRes.text())
      }

      if (usersRes && usersRes.ok) {
        const usersData = await usersRes.json()
        console.log('✅ Users loaded:', usersData);
        setUsers(Array.isArray(usersData) ? usersData : usersData.users || [])
      } else if (usersRes) {
        console.error('❌ Failed to fetch users:', await usersRes.text())
      }

      if (biddingRes.ok) {
        const biddingData = await biddingRes.json()
        console.log('✅ Bidding sheets loaded:', biddingData);
        console.log('🔍 Bidding data type:', typeof biddingData, 'isArray:', Array.isArray(biddingData));
        console.log('🔍 biddingData.biddingSheets:', biddingData.biddingSheets);
        const sheets = Array.isArray(biddingData) ? biddingData : biddingData.biddingSheets || [];
        console.log('🔍 Final bidding sheets to set:', sheets, 'count:', sheets.length);
        setBiddingSheets(sheets)
      } else {
        console.error('❌ Failed to fetch bidding sheets:', await biddingRes.text())
      }

    } catch (error) {
      // Ошибки от apiFetch уже обработаны (redirect на login при 401/403)
      if (error instanceof Error && (
        error.message.includes('Token expired') || 
        error.message.includes('Authentication failed') ||
        error.message.includes('redirecting to login')
      )) {
        console.log('🔒 Auth error, clearing session...', error.message);
        handleLogout();
        return;
      }
      console.error('❌ Error fetching data:', error)
      showError('Ошибка загрузки данных', 'Проверьте соединение с сервером');
    }
  }

  useEffect(() => {
    setSelectedArtistIds(new Set());
    setLastSelectedId(null);
  }, [filters, sortOption]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if focus is on input, textarea, or select elements
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      // Delete key for selected artists
      if (event.key === 'Delete' && selectedArtistIds.size > 0) {
        event.preventDefault();
        
        // Check if user has delete permissions
        if (currentRole && canDeleteArtists(currentRole)) {
          const selectedArtists = artists.filter(artist => selectedArtistIds.has(artist.id));
          if (selectedArtists.length === 1) {
            // Single artist selected - show confirmation
            handleDeleteArtist(selectedArtists[0].id);
          } else if (selectedArtists.length > 1) {
            // Multiple artists selected - show warning
            showWarning('Please select only one artist to delete, or use individual delete buttons.');
          }
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedArtistIds, artists, currentRole]);

  const handleLogin = (userData: User, token: string) => {
    localStorage.setItem('spellbook_token', token)
    localStorage.setItem('spellbook_user', JSON.stringify(userData))

    loadUserPreferencesFromServer().then(() => {
      loadCustomTheme();
      setUser(userData)
      setCurrentRole(userData.role);
      navigate('/artists', { replace: true });

      if (userData.mustChangePassword) {
        setMustChangePassword(true);
      } else {
        fetchData();
      }
    });
  }

  const handlePasswordChanged = () => {
    // Update user object
    if (user) {
      const updatedUser = { ...user, mustChangePassword: false };
      setUser(updatedUser);
      localStorage.setItem('spellbook_user', JSON.stringify(updatedUser));
    }
    
    // Close modal and load data
    setMustChangePassword(false);
    fetchData();
    
    // Show success message
    showSuccess('Пароль успешно изменён', 'Теперь вы можете пользоваться системой');
  };

  const handleLogout = () => {
    setUser(null)
    setCurrentRole(null)
    localStorage.removeItem('spellbook_token')
    localStorage.removeItem('spellbook_user')
    clearAllNotifications();
  }

  const handleBackupManagement = () => {
    setIsSystemMenuOpen(true);
  };

  const handleSystemMenuClose = () => {
    setIsSystemMenuOpen(false);
  };

  const handleAddArtistClick = () => {
    setIsProfileWizardOpen(true);
  };

  const handleEditArtist = (artist: Artist) => {
    setArtistToEdit(artist);
    setIsArtistModalOpen(true);
  };

  const handleAddUsersToProject = async (projectId: string, userIds: string[]) => {
    await handleAssignToProject(userIds, projectId);
  };

  const handleOpenAddUsersModal = (project: Project) => {
    console.log('🔍 Opening AddUsersModal for project:', project.name, project.id);
    setAddUsersModal({
      isOpen: true,
      projectId: project.id,
      projectName: project.name
    });
  };

  const handleRemoveFromProject = (projectId: string, artistId: string) => {
    setArtists(prev => prev.map(artist => {
      if (artist.id === artistId) {
        return {
          ...artist,
          assignedProjects: (artist.assignedProjects || []).filter(id => id !== projectId),
        };
      }
      return artist;
    }));
  };

  const handleAssignToProject = async (artistIds: string[], projectId: string) => {
    try {
      const response = await apiPost(API_CONFIG.PROJECTS.ASSIGN_ARTISTS(projectId), { artistIds });

      if (response.ok) {
        showSuccess('Users added to project successfully');
        
        // Refresh projects data
        await fetchData();
        
        // Update local state: add projectId to each artist's assignedProjects
        setArtists(prev => prev.map(artist => {
          if (artistIds.includes(artist.id)) {
            const updatedProjects = [...(artist.assignedProjects || [])];
            if (!updatedProjects.includes(projectId)) {
              updatedProjects.push(projectId);
            }
            return { ...artist, assignedProjects: updatedProjects };
          }
          return artist;
        }));
        
        const project = projects.find(p => p.id === projectId);
        showSuccess(
          'Назначение выполнено', 
          `${artistIds.length} ${artistIds.length === 1 ? 'профиль назначен' : 'профилей назначено'} на проект "${project?.name || projectId}"`
        );
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to assign artists:', response.status, errorText);
        
        try {
          const errorData = JSON.parse(errorText);
          showError('Ошибка назначения', errorData.error || errorText);
        } catch {
          showError('Ошибка назначения', errorText || `HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('❌ Error assigning artists to project:', error);
      showError('Ошибка', 'Не удалось назначить профили на проект. Проверьте подключение к серверу.');
    }
  };

  const handleDeleteArtist = (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    setDeleteArtistConfirmation({
      isOpen: true,
      artistId: artistId,
      artistName: artist?.name || 'Неизвестный художник'
    });
  };

  const handleConfirmDeleteArtist = async () => {
    if (!deleteArtistConfirmation.artistId) return;

    try {
      const response = await apiDelete(API_CONFIG.ARTISTS.DELETE(deleteArtistConfirmation.artistId));

      if (response.ok) {
        console.log('✅ Artist deleted successfully');
        setArtists(prev => prev.filter(artist => artist.id !== deleteArtistConfirmation.artistId));
        setDeleteArtistConfirmation({ isOpen: false, artistId: null, artistName: null });
      } else {
        console.error('❌ Failed to delete artist');
        alert('Failed to delete artist');
      }
    } catch (error) {
      console.error('❌ Error deleting artist:', error);
      alert('Error deleting artist');
    }
  };

  const handleCancelDeleteArtist = () => {
    setDeleteArtistConfirmation({ isOpen: false, artistId: null, artistName: null });
  };

  // Project handlers
  const handleEditProject = (project: Project) => {
    setProjectToEdit(project);
    setIsProjectModalOpen(true);
  };

  const handleCreateBidding = (project: Project, mode: 'video' | 'manual' = 'video') => {
    setSelectedProjectForBidding(project);
    setBiddingMode(mode);
    setIsBiddingModalOpen(true);
  };

  const handleSaveBidding = async (biddingData: any) => {
    try {
      // Refresh bidding sheets list
      await fetchData();
      showSuccess('Bidding sheet created successfully!');
      setIsBiddingModalOpen(false);
      setSelectedProjectForBidding(null);
      // Navigate to the newly created bidding sheet
      if (biddingData.id) {
        navigate(`/bidding/${biddingData.id}`);
      }
    } catch (error) {
      showError('Failed to create bidding sheet');
    }
  };

  const handleEditBidding = (bidding: any) => {
    // Set selected bidding sheet immediately for instant UI update
    setSelectedBiddingSheet(bidding);
    // Navigate to bidding sheet URL
    navigate(`/bidding/${bidding.id}`);
  };

  const handleDeleteBidding = async (biddingId: string) => {
    try {
      const response = await apiDelete(`${API_CONFIG.API_URL}/bidding/${biddingId}`);

      if (response.ok) {
        setBiddingSheets(biddingSheets.filter(b => b.id !== biddingId));
        showSuccess('Bidding sheet moved to trash');
      } else {
        throw new Error('Failed to delete bidding sheet');
      }
    } catch (error) {
      showError('Failed to delete bidding sheet');
      console.error('Delete bidding error:', error);
    }
  };

  const handleDuplicateBidding = async (biddingId: string) => {
    try {
      const response = await apiPost(`${API_CONFIG.API_URL}/bidding/${biddingId}/duplicate`);

      if (response.ok) {
        const duplicatedBidding = await response.json();
        showSuccess('Bidding sheet duplicated successfully!');
        fetchData(); // Refresh bidding sheets list
      } else {
        throw new Error('Failed to duplicate bidding sheet');
      }
    } catch (error) {
      showError('Failed to duplicate bidding sheet');
      console.error('Duplicate bidding error:', error);
    }
  };

  const handleRenameBidding = async (biddingId: string, newName: string) => {
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/bidding/${biddingId}/rename`, {
        method: 'PATCH',
        body: JSON.stringify({ name: newName })
      });

      if (response.ok) {
        const updatedBidding = await response.json();
        setBiddingSheets(biddingSheets.map(b => b.id === biddingId ? { ...b, name: newName } : b));
        showSuccess('Bidding sheet renamed successfully!');
      } else {
        throw new Error('Failed to rename bidding sheet');
      }
    } catch (error) {
      showError('Failed to rename bidding sheet');
      console.error('Rename bidding error:', error);
    }
  };

  const handleUpdateBiddingStatus = async (biddingId: string, status: string) => {
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/bidding/${biddingId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        const updatedBidding = await response.json();
        setBiddingSheets(biddingSheets.map(b => b.id === biddingId ? { ...b, status: status as any } : b));
        showSuccess(status === 'hidden' ? 'Смета скрыта' : 'Смета показана');
      } else {
        throw new Error('Failed to update bidding status');
      }
    } catch (error) {
      showError('Ошибка обновления статуса сметы');
      console.error('Update bidding status error:', error);
    }
  };

  const handleInviteToProject = (artistIds: string[]) => {
    setArtistsToInvite(artistIds);
    setIsProjectInvitationModalOpen(true);
  };

  const handleSendProjectInvitation = async (
    projectId: string, 
    artistIds: string[], 
    method: 'email' | 'telegram', 
    hideProjectName: boolean
  ) => {
    try {
      const response = await apiPost(`${API_CONFIG.API_URL}/invitations/send`, {
          projectId,
          artistIds,
          method,
          hideProjectName
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(`Invitation sent to ${result.sentCount} artist(s)!`);
        setIsProjectInvitationModalOpen(false);
        setArtistsToInvite([]);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitation');
      }
    } catch (error) {
      showError('Failed to send invitation');
      console.error('Send invitation error:', error);
    }
  };

  const handleDeleteProjectConfirm = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    setDeleteProjectConfirmation({
      isOpen: true,
      projectId: projectId,
      projectName: project?.name || 'Неизвестный проект'
    });
  };

  const handleConfirmDeleteProject = async () => {
    if (!deleteProjectConfirmation.projectId) return;

    await handleDeleteProject(deleteProjectConfirmation.projectId);
    setDeleteProjectConfirmation({ isOpen: false, projectId: null, projectName: null });
  };

  const handleCancelDeleteProject = () => {
    setDeleteProjectConfirmation({ isOpen: false, projectId: null, projectName: null });
  };
  
  const handleSaveArtist = async (artist: Artist, avatarFile?: File | string) => {
    try {
      // Check if we have a file to upload or need to delete avatar
      const hasAvatarFile = avatarFile instanceof File;
      const shouldDeleteAvatar = avatarFile === 'DELETE';
      
      console.log('🔍 Saving artist data:', {
        ...artist,
        avatarFile: hasAvatarFile ? `File: ${(avatarFile as File).name}` : shouldDeleteAvatar ? 'DELETE AVATAR' : 'No change'
      });

      let response;
      
      if (hasAvatarFile) {
        // Use FormData for file upload
        const formData = new FormData();
        
        // Add avatar file
        formData.append('avatar', avatarFile);
        
        // Add all artist data to FormData
        Object.keys(artist).forEach(key => {
          if (key === 'id') {
            // Skip ID for FormData
            return;
          } else if (Array.isArray(artist[key as keyof Artist])) {
            const arrayValue = JSON.stringify(artist[key as keyof Artist]);
            console.log(`📤 [FormData] ${key}:`, arrayValue);
            formData.append(key, arrayValue);
          } else if (artist[key as keyof Artist] !== null && artist[key as keyof Artist] !== undefined) {
            formData.append(key, String(artist[key as keyof Artist]));
          }
        });

        console.log('📤 Uploading with FormData (includes avatar file)');

        if (artistToEdit) {
          // Update existing artist with file
          response = await apiFetch(API_CONFIG.ARTISTS.UPDATE(artist.id), {
            method: 'PUT',
            body: formData
            // Don't set Content-Type for FormData - browser will set it automatically
          });
        } else {
          // Create new artist with file
          response = await apiFetch(API_CONFIG.ARTISTS.CREATE, {
            method: 'POST',
            body: formData
            // Don't set Content-Type for FormData - browser will set it automatically
          });
        }
      } else {
        // Use JSON for data without file
        const { id, ...artistData } = artist;
        
        // Add avatar deletion signal if needed
        const dataToSend = shouldDeleteAvatar 
          ? { ...artistData, avatarUrl: 'DELETE' }
          : artistData;
        
        console.log('📤 Sending JSON data:', shouldDeleteAvatar ? 'with DELETE signal' : 'no avatar change');

        if (artistToEdit) {
          // Update existing artist without file
          response = await apiPut(API_CONFIG.ARTISTS.UPDATE(artist.id), dataToSend);
        } else {
          // Create new artist without file
          response = await apiPost(API_CONFIG.ARTISTS.CREATE, dataToSend);
        }
      }

      if (response.ok) {
        const savedArtist = await response.json();
        console.log('✅ Artist saved successfully:', savedArtist);
        
        // Force refresh avatar URL to bypass browser cache
        if (savedArtist.avatarUrl && (hasAvatarFile || shouldDeleteAvatar)) {
          const avatarUrlWithTimestamp = savedArtist.avatarUrl.includes('?') 
            ? savedArtist.avatarUrl.split('?')[0] + `?t=${Date.now()}`
            : `${savedArtist.avatarUrl}?t=${Date.now()}`;
          savedArtist.avatarUrl = avatarUrlWithTimestamp;
          console.log('🔄 Forced avatar refresh with timestamp:', savedArtist.avatarUrl);
        }
        
        if (artistToEdit) {
          setArtists(prev => prev.map(a => a.id === artist.id ? savedArtist : a));
          setArtistToEdit(savedArtist); // Update the edited artist with fresh data
          
          // Обновляем preview в модальном окне перед закрытием
          if (hasAvatarFile && savedArtist.avatarUrl) {
            // Модальное окно обновится через useEffect когда artistToEdit изменится
            setTimeout(() => {
              setIsArtistModalOpen(false);
              setArtistToEdit(null);
            }, 100);
          } else {
            setIsArtistModalOpen(false);
            setArtistToEdit(null);
          }
          
          showSuccess('Профиль обновлен', `Профиль художника "${artist.name}" успешно обновлен`);
        } else {
          setArtists(prev => [...prev, savedArtist]);
          setIsArtistModalOpen(false);
          setArtistToEdit(null);
          showSuccess('Художник добавлен', `Профиль художника "${artist.name}" успешно создан`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to save artist:', response.status, errorText);
        
        // Try to parse JSON error response
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.details && Array.isArray(errorData.details)) {
            const validationErrors = errorData.details.map((d: any) => `${d.field}: ${d.message}`).join(', ');
            showError('Ошибка валидации', validationErrors);
          } else {
            showError('Ошибка сохранения', errorData.error || errorText);
          }
        } catch {
          showError('Ошибка сохранения', errorText);
        }
        return;
      }

      setIsArtistModalOpen(false);
      setArtistToEdit(null);
    } catch (error) {
      console.error('❌ Error saving artist:', error);
      showError('Ошибка подключения', 'Не удалось подключиться к серверу');
    }
  };

  const handleCreateProject = async (project: Omit<Project, 'id'>): Promise<Project> => {
    try {
      console.log('🔍 Creating project with data:', project);
      
      const response = await apiPost(API_CONFIG.PROJECTS.CREATE, project);

      if (response.ok) {
        const newProject = await response.json();
        console.log('✅ Project created successfully:', newProject);
        setProjects(prev => [...prev, newProject]);
        showSuccess('Проект создан', `Проект "${project.name}" успешно создан`);
        // Refresh projects list to ensure UI is updated
        await fetchData();
        return newProject;
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to create project:', response.status, errorText);
        
        try {
          const errorData = JSON.parse(errorText);
          showError('Ошибка создания проекта', errorData.error || errorText);
          throw new Error(errorData.error || errorText);
        } catch {
          showError('Ошибка создания проекта', errorText || `HTTP ${response.status}`);
          throw new Error(errorText || `HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('❌ Error creating project:', error);
      showError('Ошибка', 'Не удалось создать проект. Проверьте подключение к серверу.');
      throw error;
    }
  };

  const handleUpdateProject = async (updatedProject: Project): Promise<void> => {
    try {
      // Исключаем id из тела запроса
      const { id, ...projectData } = updatedProject;
      
      console.log('🔍 Updating project:', projectData);
      
      const response = await apiPut(API_CONFIG.PROJECTS.UPDATE(updatedProject.id), projectData);

      if (response.ok) {
        const updated = await response.json();
        console.log('✅ Project updated successfully:', updated);
        setProjects(prev => prev.map(p => p.id === updatedProject.id ? updated : p));
        if (updated.currency && selectedBiddingSheet?.projectId === updated.id) {
          setSelectedBiddingSheet((prev: typeof selectedBiddingSheet) =>
            prev ? { ...prev, currency: updated.currency, projectCurrency: updated.currency } : prev
          );
        }
        showSuccess('Проект обновлён', `Проект "${updated.name}" успешно обновлён`);
        // Refresh projects list to ensure UI is updated
        await fetchData();
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to update project:', response.status, errorText);
        
        try {
          const errorData = JSON.parse(errorText);
          showError('Ошибка обновления проекта', errorData.error || errorText);
          throw new Error(errorData.error || errorText);
        } catch {
          showError('Ошибка обновления проекта', errorText || `HTTP ${response.status}`);
          throw new Error(errorText || `HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('❌ Error updating project:', error);
      showError('Ошибка', 'Не удалось обновить проект. Проверьте подключение к серверу.');
      throw error;
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      const response = await apiDelete(API_CONFIG.PROJECTS.DELETE(projectId));

      if (response.ok) {
        console.log('✅ Project deleted successfully');
        const deletedProject = projects.find(p => p.id === projectId);
        setProjects(prev => prev.filter(p => p.id !== projectId));
        // Also remove this project from any artist assigned to it
        setArtists(prevArtists => prevArtists.map(artist => ({
          ...artist,
          assignedProjects: artist.assignedProjects.filter(id => id !== projectId),
        })));
        showSuccess('Проект удалён', `Проект "${deletedProject?.name || ''}" успешно удалён`);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to delete project:', response.status, errorText);
        
        try {
          const errorData = JSON.parse(errorText);
          showError('Ошибка удаления проекта', errorData.error || errorText);
        } catch {
          showError('Ошибка удаления проекта', errorText || `HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      showError('Ошибка', 'Не удалось удалить проект. Проверьте подключение к серверу.');
    }
  };

  const sortedAndFilteredArtists = useMemo(() => {
    // Получаем сегодняшнюю дату без проблем с часовым поясом
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    // Создаем объект today для других операций
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Логируем текущую дату для отладки
    console.log('📅 Current date info:', {
      now: now.toString(),
      today: today.toString(),
      todayStr,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      date: now.getDate(),
      timezoneOffset: now.getTimezoneOffset()
    });
    
    // Функция для проверки доступности сегодня
    const isAvailableToday = (availability: string[]): boolean => {
      if (availability.length === 0) return false;
      
      // Фильтруем и нормализуем даты
      const validDates = availability
        .map(dateStr => {
          try {
            // Нормализуем дату к формату YYYY-MM-DD
            const normalized = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
            const date = new Date(normalized + 'T00:00:00');
            return isNaN(date.getTime()) ? null : normalized;
          } catch {
            return null;
          }
        })
        .filter(Boolean) as string[];
      
      if (validDates.length === 0) return false;
      
      // Найдем самую раннюю и самую позднюю даты
      const sortedDates = validDates.sort();
      const earliestDate = sortedDates[0];
      const latestDate = sortedDates[sortedDates.length - 1];
      
      // Доступен сегодня если:
      // 1. Уже доступен (earliestDate <= today <= latestDate)
      // 2. ИЛИ начинает быть доступным завтра (для планирования)
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
      
      const isAvailableToday = earliestDate && earliestDate <= todayStr && latestDate >= todayStr;
      const startsFromTomorrow = earliestDate && earliestDate === tomorrowStr && latestDate >= tomorrowStr;
      
      const isAvailable = Boolean(isAvailableToday || startsFromTomorrow);
      
      // Отладочный лог (только если фильтр активен)
      if (filters.availableToday) {
        const hasStarted = earliestDate <= todayStr;
        const hasNotEnded = latestDate >= todayStr;
        const isOutdated = latestDate < todayStr;
        
        console.log('🔍 Availability check for artist:', {
          rawAvailability: availability,
          validDates: sortedDates,
          earliestDate,
          latestDate,
          todayStr,
          tomorrowStr,
          hasStarted,
          hasNotEnded,
          isOutdated,
          isAvailable,
          status: isAvailable ? '✅ Available today/tomorrow' : (isOutdated ? '🟠 Outdated' : '❌ Not available yet'),
          // Дополнительная отладка для дат
          todayActual: todayStr,
          comparison: `${earliestDate} <= ${todayStr} <= ${latestDate}`,
          // Проверяем каждое условие отдельно
          conditions: {
            hasEarliestDate: !!earliestDate,
            isAvailableToday,
            startsFromTomorrow,
            finalResult: isAvailable
          }
        });
      }
      
      return isAvailable;
    };
    
    const filtered = artists.filter(artist => {
      const nameMatch = artist.name.toLowerCase().includes(filters.name.toLowerCase());
      const specializationMatch = !filters.specialization || artist.specialization === filters.specialization;
      const softwareMatch = filters.software.length === 0 || filters.software.every(s => artist.software.includes(s));
      const availabilityMatch = !filters.availableToday || isAvailableToday(artist.availability);
      const projectMatch = filters.project === 'ALL_PROFILES' || !filters.project || artist.assignedProjects.includes(filters.project);
      const profileTypeMatch = !filters.profileType || artist.profileType === filters.profileType;
      const registrationMatch =
        !filters.registrationStatus || artist.registrationStatus === filters.registrationStatus;

      return nameMatch && specializationMatch && softwareMatch && availabilityMatch && projectMatch && profileTypeMatch && registrationMatch;
    });

    switch (sortOption) {
      case SortOption.NAME_ASC:
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case SortOption.NAME_DESC:
        return filtered.sort((a, b) => b.name.localeCompare(a.name));
      case SortOption.SPECIALIZATION_ASC:
        return filtered.sort((a, b) => a.specialization.localeCompare(b.specialization));
      case SortOption.SPECIALIZATION_DESC:
        return filtered.sort((a, b) => b.specialization.localeCompare(a.specialization));
      default:
        return filtered;
    }

  }, [artists, filters, sortOption]);

  // Фильтрация и сортировка проектов
  const filteredProjects = useMemo(() => {
    const filtered = projects.filter(project => {
      // Name filter (searches in project name and description)
      const nameMatch = !filters.name || 
        project.name.toLowerCase().includes(filters.name.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(filters.name.toLowerCase()));
      
      // Client filter
      const clientMatch = !filters.client || 
        (project.client && project.client.toLowerCase().includes(filters.client.toLowerCase()));
      
      // Status filter
      const statusMatch = !filters.status || project.status === filters.status;
      
      // Priority filter
      const priorityMatch = !filters.priority || project.priority === filters.priority;
      
      // Project type filter
      const projectTypeMatch = !filters.projectType || project.projectType === filters.projectType;
      
      // Producer filter
      const producerMatch = !filters.producerId || project.producerId === filters.producerId;
      
      // Manager filter
      const managerMatch = !filters.managerId || project.managerId === filters.managerId;
      
      return nameMatch && clientMatch && statusMatch && priorityMatch && projectTypeMatch && producerMatch && managerMatch;
    });
    
    // Sort projects
    switch (projectSortOption) {
      case SortOption.NAME_ASC:
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case SortOption.NAME_DESC:
        return filtered.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return filtered;
    }
  }, [projects, filters, projectSortOption]);
  
  // Фильтрация и сортировка bidding sheets
  const sortedBiddingSheets = useMemo(() => {
    // Filter bidding sheets
    const filtered = biddingSheets.filter(sheet => {
      // Hide hidden sheets from Client/Demo users
      if (isClientRole(currentRole) && sheet.status === 'hidden') {
        return false;
      }
      
      // Name filter (searches in bidding sheet name and project name)
      const nameMatch = !filters.name || 
        (sheet.name && sheet.name.toLowerCase().includes(filters.name.toLowerCase())) ||
        (sheet.projectName && sheet.projectName.toLowerCase().includes(filters.name.toLowerCase()));
      
      // Project filter
      const projectMatch = !filters.biddingProject || sheet.projectId === filters.biddingProject;
      
      return nameMatch && projectMatch;
    });
    
    // Sort bidding sheets
    switch (biddingSortOption) {
      case SortOption.NAME_ASC:
        return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case SortOption.NAME_DESC:
        return filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      default:
        return filtered;
    }
  }, [biddingSheets, biddingSortOption, filters.name, filters.biddingProject]);

  const handleArtistSelect = (artistId: string, event: React.MouseEvent) => {
    const newSelectedIds = new Set(selectedArtistIds);

    if (event.shiftKey && lastSelectedId) {
        const lastIndex = sortedAndFilteredArtists.findIndex(a => a.id === lastSelectedId);
        const currentIndex = sortedAndFilteredArtists.findIndex(a => a.id === artistId);

        if (lastIndex !== -1 && currentIndex !== -1) {
            const start = Math.min(lastIndex, currentIndex);
            const end = Math.max(lastIndex, currentIndex);
            for (let i = start; i <= end; i++) {
                newSelectedIds.add(sortedAndFilteredArtists[i].id);
            }
        }
    } else {
        if (newSelectedIds.has(artistId)) {
            newSelectedIds.delete(artistId);
        } else {
            newSelectedIds.add(artistId);
        }
    }
    
    setSelectedArtistIds(newSelectedIds);
    setLastSelectedId(artistId);
  };

  const handleClearSelection = () => {
    if (selectedArtistIds.size > 0) {
      setSelectedArtistIds(new Set());
      setLastSelectedId(null);
    }
  };

  const handleMainClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-artist-id]')) {
      handleClearSelection();
    }
  };

  // Ref to store current bidding export data
  const biddingExportDataRef = React.useRef<any>(null);
  const [isExportExcelOpen, setIsExportExcelOpen] = React.useState(false);
  const [excelExportPending, setExcelExportPending] = React.useState<
    | { kind: 'current' }
    | { kind: 'byId'; biddingId: string; mode: 'revenue' | 'expense' | 'all' }
    | null
  >(null);

  const runCurrentBiddingExcelExport = async (audience: ExcelExportAudience) => {
    const data = biddingExportDataRef.current;
    if (!data) return;
    const lang = localStorage.getItem('spellbook_language') || 'ru';
    const { exportBiddingToExcel } = await import('./utils/excelExport');
    await exportBiddingToExcel(
      data.biddingSheet,
      data.shots,
      data.assignments,
      data.artists,
      data.specializations,
      '',
      lang,
      audience
    );
  };

  const handleExportExcel = () => {
    if (contentView === 'users' || contentView === 'artists') {
      import('./utils/excelExport').then(({ exportArtistsToExcel }) => {
        exportArtistsToExcel(sortedAndFilteredArtists);
      });
    } else if (contentView === 'projects') {
      import('./utils/excelExport').then(({ exportProjectsToExcel }) => {
        exportProjectsToExcel(filteredProjects);
      });
    } else if (contentView === 'bidding' && biddingExportDataRef.current) {
      if (isClientRole(currentRole)) {
        runCurrentBiddingExcelExport('client').catch((error) => {
          console.error('Failed to export bidding sheet:', error);
          alert('Failed to export Excel file. Please try again.');
        });
        return;
      }
      setExcelExportPending({ kind: 'current' });
      setIsExportExcelOpen(true);
    }
  };
  
  const runExportBiddingById = async (
    biddingId: string,
    mode: 'revenue' | 'expense' | 'all' = 'all',
    audience: ExcelExportAudience = 'internal'
  ) => {
    try {
      // Fetch initial bidding sheet to resolve IDs
      const initialResponse = await apiGet(`${API_CONFIG.API_URL}/bidding/${biddingId}`);
      
      if (!initialResponse.ok) {
        throw new Error('Failed to fetch bidding sheet info');
      }
      
      const initialSheet = await initialResponse.json();
      let targetId = biddingId;
      const effectiveMode = audience === 'client' ? 'revenue' : mode;

      // Resolve target ID based on mode
      if (effectiveMode === 'revenue') {
        if (initialSheet.isRevenueSheet === false && initialSheet.revenueSheetId) {
          targetId = initialSheet.revenueSheetId;
        } 
        // Fallback to current sheet if linked sheet not found (Hub & Spoke model)
      } else if (effectiveMode === 'expense') {
        if ((initialSheet.isRevenueSheet === true || initialSheet.isRevenueSheet === undefined) && initialSheet.expenseSheetId) {
          targetId = initialSheet.expenseSheetId;
        }
        // Fallback to current sheet if linked sheet not found (Hub & Spoke model)
      }

      // Fetch target bidding sheet data
      const biddingResponse = await apiGet(`${API_CONFIG.API_URL}/bidding/${targetId}`);
      
      if (!biddingResponse.ok) {
        throw new Error('Failed to fetch bidding sheet');
      }
      
      const biddingSheet = await biddingResponse.json();
      
      // Fetch shots
      const shotsResponse = await apiGet(`${API_CONFIG.API_URL}/bidding/${targetId}/shots`);
      
      if (!shotsResponse.ok) {
        throw new Error('Failed to fetch shots');
      }
      
      const shotsData = await shotsResponse.json();
      const shots = Array.isArray(shotsData) ? shotsData : (shotsData.shots || []);
      
      // Fetch assignments
      const assignmentsResponse = await apiGet(`${API_CONFIG.API_URL}/bidding/${targetId}/assignments?type=${effectiveMode}`);
      
      if (!assignmentsResponse.ok) {
        throw new Error('Failed to fetch assignments');
      }
      
      const assignments = await assignmentsResponse.json();
      
      // Fetch specializations
      const specsResponse = await apiGet(`${API_CONFIG.API_URL}/bidding/${targetId}/specializations`);
      
      const specsData = specsResponse.ok ? await specsResponse.json() : [];
      // Handle both array and object wrapper response formats
      const specializations = Array.isArray(specsData) ? specsData : (specsData.specializations || []);
      const specNames = specializations.map((s: any) => s.name);
      
      // Fetch artists
      const artistsResponse = await apiGet(`${API_CONFIG.API_URL}/artists`);
      
      if (!artistsResponse.ok) {
        throw new Error('Failed to fetch artists');
      }
      
      const artistsData = await artistsResponse.json();
      const artists = Array.isArray(artistsData) ? artistsData : (artistsData.artists || []);
      
      // Export to Excel
      const lang = localStorage.getItem('spellbook_language') || 'ru';
      const { exportBiddingToExcel, exportCombinedBiddingToExcel } = await import('./utils/excelExport');
      
      if (audience === 'client') {
        await exportBiddingToExcel(
          biddingSheet,
          shots,
          assignments,
          artists,
          specNames,
          '',
          lang,
          'client'
        );
      } else if (effectiveMode === 'all') {
        // Combined export: revenue + expense in one workbook with tabs
        const revenueAssignments = assignments.filter((a: any) => a.type === 'REVENUE');
        const expenseAssignments = assignments.filter((a: any) => a.type === 'EXPENSE');
        
        await exportCombinedBiddingToExcel(
          biddingSheet,
          shots,
          revenueAssignments,
          expenseAssignments,
          artists,
          specNames,
          lang,
          'internal'
        );
      } else {
        // Single file export with suffix
        const suffix = effectiveMode === 'revenue' ? 'Revenue' : (effectiveMode === 'expense' ? 'Expense' : '');
        
        await exportBiddingToExcel(
          biddingSheet,
          shots,
          assignments,
          artists,
          specNames,
          suffix,
          lang,
          'internal'
        );
      }
      
      showSuccess(`Excel file exported (${audience === 'client' ? 'client' : effectiveMode === 'all' ? 'current' : effectiveMode}) successfully!`);
    } catch (error) {
      console.error('Failed to export bidding sheet:', error);
      showError('Failed to export Excel file. Please try again.');
      throw error;
    }
  };

  const handleExportBiddingById = (
    biddingId: string,
    mode: 'revenue' | 'expense' | 'all' = 'all'
  ) => {
    if (isClientRole(currentRole)) {
      void runExportBiddingById(biddingId, 'revenue', 'client');
      return;
    }
    setExcelExportPending({ kind: 'byId', biddingId, mode });
    setIsExportExcelOpen(true);
  };

  const handleExcelExportModalConfirm = async (audience: ExcelExportAudience) => {
    if (!excelExportPending) return;
    if (excelExportPending.kind === 'current') {
      await runCurrentBiddingExcelExport(audience);
      showSuccess('Excel file exported successfully!');
      return;
    }
    await runExportBiddingById(
      excelExportPending.biddingId,
      excelExportPending.mode,
      audience
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <div className="loading-title">SpellBook DB</div>
          <div className="loading-text">Загрузка системы управления художниками...</div>
        </div>
      </div>
    )
  }

  if (!user || !currentRole) {
    return <LoginScreen onLogin={handleLogin} />
  }

  const isBiddingTableOpen = !!(contentView === 'bidding' && selectedBiddingSheet);

  return (
    <div
      className={`bg-gray-900 text-gray-100 font-sans ${
        isBiddingTableOpen
          ? 'h-screen overflow-hidden flex flex-col'
          : 'min-h-screen'
      }`}
    >
      <div className="shrink-0">
      <Header 
        currentRole={currentRole} 
        currentUser={user}
        onLogout={handleLogout} 
        onAddArtist={handleAddArtistClick}
        onManageProjects={() => setIsProjectModalOpen(true)}
        onExportExcel={handleExportExcel}
        onBackupManagement={() => setIsSystemMenuOpen(true)}
        selectedCount={selectedArtistIds.size}
        currentView={contentView}
        activeUsers={activeUsers}
        currentBiddingSheetId={contentView === 'bidding' && selectedBiddingSheet ? selectedBiddingSheet.id : null}
      />
      
      {/* Content View Toggle, View Mode Toggle and Sort Toggle - below header */}
      <div className="px-4 md:px-8 pt-4 pb-2 flex justify-between items-center gap-4">
        {/* Left: Content View Toggle (Users/Projects) */}
        <ContentViewToggle 
          currentView={contentView}
          currentRole={currentRole}
        />
        
        {/* Right: Sort and View Mode toggles for artists/profiles view */}
        {(contentView === 'artists' || contentView === 'users') && (
          <div className="flex gap-2">
            <SortToggle 
              sortOption={sortOption}
              onSortChange={setSortOption}
            />
            <ViewModeToggle 
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
            />
          </div>
        )}
        {contentView === 'projects' && (
          <div className="flex gap-2">
            <SortToggle 
              sortOption={projectSortOption}
              onSortChange={setProjectSortOption}
            />
            <ViewModeToggle 
              viewMode={projectViewMode}
              onViewModeChange={handleProjectViewModeChange}
            />
          </div>
        )}
        {contentView === 'teams' && (
          <div className="flex gap-2">
            <SortToggle 
              sortOption={teamsSortOption}
              onSortChange={setTeamsSortOption}
            />
          </div>
        )}
        {contentView === 'bidding' && !selectedBiddingSheet && (
          <div className="flex gap-2">
            <SortToggle 
              sortOption={biddingSortOption}
              onSortChange={setBiddingSortOption}
            />
            <ViewModeToggle 
              viewMode={biddingViewMode}
              onViewModeChange={handleBiddingViewModeChange}
            />
          </div>
        )}
      </div>
      </div>
      
      <main
        className={`px-4 md:px-8 py-4 ${
          isBiddingTableOpen
            ? 'flex-1 min-h-0 overflow-hidden flex flex-col'
            : 'pb-40'
        }`}
        onClick={handleMainClick}
      >
        {(contentView === 'users' || contentView === 'artists') ? (
          <ArtistList 
            artists={sortedAndFilteredArtists}
            projects={projects}
            currentRole={currentRole}
            onEditArtist={handleEditArtist}
            onDeleteArtist={handleDeleteArtist}
            onAssignToProject={handleAssignToProject}
            onInviteToProject={handleInviteToProject}
            onCreateArtist={handleAddArtistClick}
            viewMode={viewMode}
            selectedArtistIds={selectedArtistIds}
            onArtistSelect={handleArtistSelect}
          />
        ) : contentView === 'projects' ? (
          <ProjectList 
            projects={filteredProjects}
            currentRole={currentRole}
            currentUserId={user?.id}
            onEditProject={handleEditProject}
            onUpdateProject={handleUpdateProject}
            onDeleteProject={handleDeleteProjectConfirm}
            onCreateBidding={handleCreateBidding}
            onCreateProject={() => setIsProjectModalOpen(true)}
            onAddUsersToProject={handleOpenAddUsersModal}
            viewMode={projectViewMode}
          />
        ) : contentView === 'teams' ? (
          <TeamsView
            projects={filteredProjects}
            artists={artists}
            currentRole={currentRole}
            onEditArtist={handleEditArtist}
            onDeleteArtist={handleDeleteArtist}
            onAddUsersToProject={handleOpenAddUsersModal}
            onAssignToProject={handleAssignToProject}
            onRemoveFromProject={handleRemoveFromProject}
            sortOption={teamsSortOption}
            filters={{
              name: filters.name,
              status: filters.status,
              priority: filters.priority,
              specialization: filters.specialization,
            }}
          />
        ) : contentView === 'rates' ? (
          <RatesView currentRole={currentRole} />
        ) : contentView === 'bidding' ? (
          selectedBiddingSheet ? (
            <div className="flex-1 min-h-0 flex flex-col">
            <ShotTable
              biddingSheet={selectedBiddingSheet}
              currentRole={currentRole}
              onClose={() => {
                fetchData();
                navigate('/bidding');
              }}
              onSave={() => {
                fetchData();
                navigate('/bidding');
              }}
              onExportDataUpdate={(data) => {
                biddingExportDataRef.current = data;
              }}
            />
            </div>
          ) : (
            <BiddingList 
              biddingSheets={sortedBiddingSheets}
              currentRole={currentRole}
              currentUserId={user?.id}
              onEditBidding={handleEditBidding}
              onDeleteBidding={handleDeleteBidding}
              onDuplicateBidding={handleDuplicateBidding}
              onRenameBidding={handleRenameBidding}
              onExportBidding={handleExportBiddingById}
              onCreateBidding={(mode) => {
                setBiddingMode(mode || 'video');
                setIsBiddingModalOpen(true);
              }}
              onUpdateBiddingStatus={handleUpdateBiddingStatus}
              viewMode={biddingViewMode}
            />
          )
        ) : contentView === 'finance' ? (
          <TransactionsPage
            projects={projects}
            currentRole={currentRole}
            currentUserId={user?.id}
          />
        ) : (
          <VfxPlannerStub />
        )}
      </main>
      {!(contentView === 'bidding' && selectedBiddingSheet) && (
        <FilterBar 
          filters={filters} 
          onFilterChange={setFilters}
          projects={projects}
          users={users}
          contentView={contentView}
        />
      )}
      <ProfileWizardModal
        isOpen={isProfileWizardOpen}
        onClose={() => setIsProfileWizardOpen(false)}
        onSave={handleSaveArtist}
        onProfileCreated={(profile) => setArtists((prev) => [...prev, profile])}
        projects={projects}
      />
      <ArtistFormModal 
        isOpen={isArtistModalOpen && artistToEdit !== null}
        onClose={() => {
          setIsArtistModalOpen(false);
          setArtistToEdit(null);
        }}
        onSave={handleSaveArtist}
        artistToEdit={artistToEdit}
        projects={projects}
        onAccessUpdated={(profile) =>
          setArtists((prev) => prev.map((a) => (a.id === profile.id ? { ...a, ...profile } : a)))
        }
      />
      <ProjectManagementModal
        isOpen={isProjectModalOpen}
        onClose={() => {
          setIsProjectModalOpen(false);
          setProjectToEdit(null);
          // Force refresh projects list to update UI (especially clientName)
          fetchData();
        }}
        projects={projects}
        projectToEdit={projectToEdit}
        onCreateProject={handleCreateProject}
        onUpdateProject={handleUpdateProject}
        onDeleteProject={handleDeleteProject}
        currentRole={currentRole}
        currentUserId={user?.id}
      />

      <UserManagementModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        currentUserRole={currentRole}
      />

      <ProjectInvitationModal
        isOpen={isProjectInvitationModalOpen}
        onClose={() => {
          setIsProjectInvitationModalOpen(false);
          setArtistsToInvite([]);
        }}
        artists={artists.filter(a => artistsToInvite.includes(a.id))}
        projects={projects}
        onSendInvitation={handleSendProjectInvitation}
      />

      <ChangePasswordModal
        isOpen={mustChangePassword}
        onSuccess={handlePasswordChanged}
      />

      <ExportBiddingExcelModal
        isOpen={isExportExcelOpen}
        onClose={() => {
          setIsExportExcelOpen(false);
          setExcelExportPending(null);
        }}
        onExport={handleExcelExportModalConfirm}
      />

      {/* Модальное окно подтверждения удаления художника */}
      <ConfirmationModal
        isOpen={deleteArtistConfirmation.isOpen}
        onClose={handleCancelDeleteArtist}
        onConfirm={handleConfirmDeleteArtist}
        title="Удалить художника?"
        message="Это действие нельзя отменить. Все данные художника будут удалены из системы."
        confirmText="Удалить"
        cancelText="Отмена"
        type="danger"
        itemName={deleteArtistConfirmation.artistName || undefined}
      />

      {/* Модальное окно подтверждения удаления проекта */}
      <ConfirmationModal
        isOpen={deleteProjectConfirmation.isOpen}
        onClose={handleCancelDeleteProject}
        onConfirm={handleConfirmDeleteProject}
        title="Удалить проект?"
        message="Это действие нельзя отменить. Все данные проекта будут удалены из системы."
        confirmText="Удалить"
        cancelText="Отмена"
        type="danger"
        itemName={deleteProjectConfirmation.projectName || undefined}
      />
      
      <SystemMenu
        isOpen={isSystemMenuOpen}
        onClose={handleSystemMenuClose}
        onDataRefresh={fetchData}
        currentRole={currentRole}
      />

      {/* Bidding Modal */}
      <BiddingModal
        isOpen={isBiddingModalOpen}
        onClose={() => {
          setIsBiddingModalOpen(false);
          setSelectedProjectForBidding(null);
          setBiddingMode('video');
        }}
        onSave={handleSaveBidding}
        projects={projects}
        currentUserId={user?.id}
        mode={biddingMode}
        selectedProject={selectedProjectForBidding || undefined}
      />
      
      {/* Add Users to Project Modal */}
      <AddUsersToProjectModal
        isOpen={addUsersModal.isOpen}
        onClose={() => setAddUsersModal({ isOpen: false, projectId: '', projectName: '' })}
        projectId={addUsersModal.projectId}
        projectName={addUsersModal.projectName}
        users={artists.map(a => ({
          id: a.id,
          name: a.name,
          email: a.email,
          specialization: a.specialization
        }))}
        assignedUserIds={(() => {
          // Get already assigned user IDs for this project
          const project = projects.find(p => p.id === addUsersModal.projectId);
          if (!project) return [];
          // We'll fetch assigned users when modal opens
          return [];
        })()}
        onAddUsers={handleAddUsersToProject}
      />
      
      <NotificationContainer 
        notifications={notifications} 
        onClose={removeNotification} 
      />
    </div>
  );
}

export default App
